import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Dossier } from "@rumble-it/common-lib/types/models/Dossier.interface";
import type { Task } from "@rumble-it/common-lib/types/models/Task.interface";
import { fetchTasks } from "@/lib/api/resources/tasks";
import { useCompleteTaskMutation } from "../../tasks/lib/hooks/useCompleteTaskMutation";
import { buildTaskColumns } from "../../tasks/lib/constants/taskColumns";
import { autoGeneratedTasks } from "../data/autoGeneratedTasksData";
import { Table } from "@/components/v2";
import { Card, Button } from "@rumbledirect/ui";
import { Tag } from "antd";
import type { TableColumnsType } from "antd";
import { CheckOutlined, RobotOutlined } from "@ant-design/icons";

const AUTO_TASK_PREFIX = "auto-task-";

const isAutoGenerated = (record: Task) =>
  record.id.startsWith(AUTO_TASK_PREFIX);

export const ClaimDossierTasksTab = ({
  data,
}: {
  data: Dossier | undefined;
}) => {
  const [page, setPage] = useState(1);

  const tasksQuery = useQuery({
    queryKey: ["tasks", page],
    queryFn: () =>
      fetchTasks({
        page,
        status: "open",
        dossierId: data?.id,
      }),
  });
  const completeMutation = useCompleteTaskMutation();

  const baseColumns = buildTaskColumns(completeMutation);

  const columns: TableColumnsType<Task> = useMemo(() => {
    return baseColumns.map((col) => {
      if (col.key === "description") {
        return {
          ...col,
          render: (text: string, record: Task) => {
            const originalRender = col.render as (
              text: string,
              record: Task,
            ) => React.ReactNode;

            return (
              <>
                {isAutoGenerated(record) ? (
                  <span
                    style={
                      record.isCompleted
                        ? { color: "rgba(0, 0, 0, 0.45)" }
                        : undefined
                    }
                  >
                    {record.description}
                  </span>
                ) : (
                  originalRender(text, record)
                )}
                {isAutoGenerated(record) && record.isCompleted && (
                  <Tag
                    icon={<RobotOutlined />}
                    color="purple"
                    style={{ marginLeft: 8 }}
                  >
                    Automatisch
                  </Tag>
                )}
              </>
            );
          },
        };
      }

      if (col.key === "actions") {
        return {
          ...col,
          render: (_text: string, record: Task) => {
            if (isAutoGenerated(record) && record.isCompleted) return null;
            if (isAutoGenerated(record)) {
              return (
                <Button icon={<CheckOutlined />} onClick={() => {}}>
                  Afvinken
                </Button>
              );
            }
            const originalRender = col.render as (
              text: string,
              record: Task,
            ) => React.ReactNode;
            return originalRender(_text, record);
          },
        };
      }

      return col;
    });
  }, [baseColumns]);

  const allTasks = useMemo(() => {
    const apiTasks = tasksQuery?.data?.member ?? [];
    return [...autoGeneratedTasks, ...apiTasks];
  }, [tasksQuery?.data?.member]);

  return (
    <Card title="Taken">
      <Table
        rowKey="id"
        columns={columns}
        dataSource={allTasks}
        loading={tasksQuery.isLoading}
        pagination={{
          pageSize: 50,
          total: (tasksQuery.data?.totalItems ?? 0) + autoGeneratedTasks.length,
          showSizeChanger: false,
          onChange: (page) => setPage(page),
        }}
      />
    </Card>
  );
};
